import { lazy, Suspense, useCallback } from "react";
import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "antd5";
import classNames from "classnames";

import { CommentDto } from "components/comments/CommentComponent";
import CommentsSidebar from "components/comments/CommentsSidebar";
import { serializeComment } from "components/comments/utils";
import ContactCard from "components/form_components/ContactCard";
import { SaveLeadDrawerProvider } from "components/leads/saving/SaveLeadDrawerProvider";
import AiRecordSummary from "components/record_summary/SummarySection";
import Divider from "components/ui/divider/Divider";
import { TextHighlighter } from "lib/core_components/TextHighlighter";
import { NOTICE_INSIGHTS_RAIL, useVariableValue } from "lib/featureFlags";
import { BasicComment } from "lib/generated/app-service-gql/graphql";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { NoticeHighlights } from "lib/hooks/api/notices/useNoticeHighlight";
import { updateCachedNoticeAndList } from "lib/hooks/api/notices/utils";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { CommentObjectType } from "lib/types/graphQLEnums";
import { getAddress, simpleArrayDedupe } from "lib/utils";
import { ViewLeadsDrawerProvider } from "../leads/viewing/ViewLeadsDrawerProvider";
import { NoticeInsightsRail } from "./notice_insights/NoticeInsightsRail";
import { getRelationshipBuyer } from "./notice_relationship/relationshipPair";
import { useShowsIncumbents } from "./notice_relationship/useShowsIncumbents";
import Contracts from "./Contracts";
import CpvCodes from "./CpvCodes";
import DetailsCardContent from "./DetailsCardContent";
import LotsV2 from "./LotsV2";
import NoticeCTA from "./NoticeCTA";
import NoticeTimelineV2 from "./NoticeTimelineV2";

import css from "./NoticeDetailsContent.module.scss";

// Loaded on demand: the notice preview is part of the app-wide record viewer, so a direct import
// would put the rail, its charts and citations into every page.
const Incumbents = lazy(() =>
  import("./notice_insights/Incumbents").then((module) => ({ default: module.Incumbents })),
);

type DescriptionProps = {
  noticeDescription: string;
  highlights: string[];
};

function Description({ noticeDescription, highlights }: DescriptionProps) {
  return (
    <div className={css.description} aria-label="notice-description">
      <h2 className={css.section}>Description</h2>
      <TextHighlighter content={noticeDescription} wordsToHighlight={highlights} />
    </div>
  );
}

type NoticeDetailsContentProps = {
  notice: NoticeDetails;
  noticeHighlights: NoticeHighlights | undefined;
  trackingContextName: "Title header" | "Record preview slide-out"; // TODO: Remove as no longer needed
  containerClassName?: string;
};

export default function NoticeDetailsContent({
  notice,
  noticeHighlights,
  containerClassName,
}: NoticeDetailsContentProps) {
  const queryClient = useQueryClient();
  const currentUser = useAssertCurrentUser();
  const railEnabled = useVariableValue(NOTICE_INSIGHTS_RAIL, false);
  // The rail's one section so far; with nothing to say, the notice keeps the full width.
  const showRail = useShowsIncumbents(notice);

  const comments: CommentDto[] = (notice.comments || []) as CommentDto[];

  const onCreateComment = useCallback(
    (newComment: BasicComment) =>
      updateCachedNoticeAndList(
        queryClient,
        notice,
        (noticeData) => {
          // Guard against duplicates if the comment is already in the cache (e.g. after a refetch).
          if ((noticeData.comments || []).some((c) => c.id === newComment.id)) return noticeData;
          return {
            ...noticeData,
            comments: [serializeComment(newComment, currentUser), ...(noticeData.comments || [])],
          };
        },
        (r) => {
          if (r.comments.some((c) => c.id === newComment.id)) return r;
          return {
            ...r,
            comments: [
              {
                id: newComment.id,
                author: { guid: currentUser.guid, firstName: currentUser.firstName },
              },
              ...r.comments,
            ],
          };
        },
      ),
    [currentUser, notice, queryClient],
  );

  const onUpdateComment = useCallback(
    (updatedComment: BasicComment) =>
      updateCachedNoticeAndList(
        queryClient,
        notice,
        (noticeData) => ({
          ...noticeData,
          comments: (noticeData.comments || []).map((comment) =>
            comment.id === updatedComment.id
              ? serializeComment(updatedComment, currentUser)
              : comment,
          ),
        }),
        (r) => ({
          ...r,
          // Spread the cached row comment so any future fields survive; an edit only refreshes author.
          comments: r.comments.map((c) =>
            c.id === updatedComment.id
              ? { ...c, author: { guid: currentUser.guid, firstName: currentUser.firstName } }
              : c,
          ),
        }),
      ),
    [currentUser, notice, queryClient],
  );

  const onDeleteComment = useCallback(
    (deletedCommentId: string) =>
      updateCachedNoticeAndList(
        queryClient,
        notice,
        (noticeData) => ({
          ...noticeData,
          comments: (noticeData.comments || []).filter(
            (comment) => comment.id !== deletedCommentId,
          ),
        }),
        (r) => ({
          ...r,
          comments: r.comments.filter((c) => c.id !== deletedCommentId),
        }),
      ),
    [notice, queryClient],
  );

  const buyerAddress = getAddress(
    notice.buyers[0]?.country ?? "",
    notice.buyers[0]?.address ?? "",
    notice.buyers[0]?.town ?? "",
    notice.buyers[0]?.postalCode ?? "",
  );

  // When the notice clearly pairs one buyer with its suppliers, supplier names open that relationship.
  const relationshipBuyerGuid = getRelationshipBuyer(notice)?.guid;

  const allHighlights = noticeHighlights
    ? simpleArrayDedupe([
        ...(noticeHighlights.title || []),
        ...(noticeHighlights.description || []),
      ])
    : [];

  const summaryAndDescription = (
    <>
      {!!notice.description && notice.description.length >= 150 && (
        <AiRecordSummary aiSummary={notice.aiSummary} highlights={allHighlights} />
      )}
      <Description
        noticeDescription={notice.description || "No description available."}
        highlights={noticeHighlights?.description || []}
      />
    </>
  );

  const timelineContractsAndCodes = (
    <>
      <Divider className={css.divider} />
      <NoticeTimelineV2 notice={notice} />
      <Divider className={css.divider} />
      <Contracts contracts={notice.contracts} relationshipBuyerGuid={relationshipBuyerGuid} />
      <LotsV2 lotsv2={notice.lotsv2} />
      <Divider className={css.divider} />
      <CpvCodes cpvCodes={notice.cpvCodes} />
    </>
  );

  const details = (
    <div className={css.cardContent}>
      <h2 className={css.mediumTitle}>Details</h2>
      <DetailsCardContent
        notice={{
          guid: notice.guid,
          stage: notice.stage ?? "Unknown",
          value: notice.value ?? null,
          currency: notice.currency ?? "",
          url: notice.url,
          duplicates: notice.duplicates.map(({ guid, name, url }) => ({
            guid,
            name: name || "N/A",
            url,
          })),
          sourceNotices: notice.sourceNotices,
        }}
        buyers={notice.buyers}
        framework={notice.relatedFrameworkProcess}
        lots={notice.lots}
        score={notice.score ?? 0}
        relationshipBuyerGuid={relationshipBuyerGuid}
      />
    </div>
  );

  const contactProps = {
    name: notice.buyerContactName ?? "",
    phone: notice.buyerContactPhone ?? "",
    email: notice.buyerContactEmail ?? "",
    address: buyerAddress,
    website: notice.buyers[0]?.buyerUrl ?? "",
    buyerIds: notice.buyers.map((buyer) => buyer.guid),
    showWebsite: true,
    showContactsButton: true,
    className: css.contactLabel,
  };
  const commentsProps = {
    objectId: notice.procurementStage.id,
    objectType: CommentObjectType.ProcurementStage,
    objectName: notice.name ?? "",
    comments,
    onCreateComment,
    onUpdateComment,
    onDeleteComment,
  };

  return (
    <SaveLeadDrawerProvider trackingData={{ "Context name": "Notice details panel" }}>
      <ViewLeadsDrawerProvider>
        {railEnabled ? (
          // The notice in one inline column under one level of headings, with the rail beside it;
          // without the page's column class, whose card-layout gap would otherwise win.
          <WithRail>
            {/* No lead CTA card: the header carries its lead, bid and bid/no-bid report actions. */}
            <NoticeColumn aria-label="notice-details-column">
              {details}
              <Divider className={css.divider} />
              {summaryAndDescription}
              {timelineContractsAndCodes}
              <Divider className={css.divider} />
              <ContactCard {...contactProps} inline />
              <Divider className={css.divider} />
              <CommentsSidebar {...commentsProps} inline />
            </NoticeColumn>
            {showRail && (
              <NoticeInsightsRail>
                <Suspense fallback={null}>
                  <Incumbents notice={notice} />
                </Suspense>
              </NoticeInsightsRail>
            )}
          </WithRail>
        ) : (
          <div className={classNames(containerClassName, css.container)}>
            <div className={css.leftColumn} aria-label="notice-details-left-column">
              {summaryAndDescription}
              {timelineContractsAndCodes}
            </div>
            <div className={css.rightColumn} aria-label="notice-details-right-column">
              <NoticeCTA
                rightPosition={true}
                notice={notice}
                trackingContextName="Notice details panel"
              />
              <Card className={css.card}>{details}</Card>
              <ContactCard {...contactProps} />
              <div className={css.card}>
                <CommentsSidebar {...commentsProps} />
              </div>
            </div>
          </div>
        )}
      </ViewLeadsDrawerProvider>
    </SaveLeadDrawerProvider>
  );
}

// With the insights rail: the notice in one column, the rail a full-height panel on the right.
const WithRail = styled.div({
  display: "flex",
  width: "100%",
  minHeight: "100%",
  alignItems: "stretch",
  "@media screen and (max-width: 880px)": {
    flexDirection: "column",
  },
});

const NoticeColumn = styled.div(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: 24,
  minWidth: 0,
  padding: "24px 24px 48px",
  overflowWrap: "anywhere",

  // A readable measure, centred in whatever room the rail leaves.
  "& > *": {
    width: "100%",
    maxWidth: 720,
    marginInline: "auto",
  },

  // The divider brings its own vertical margin, which would otherwise undo the centring.
  [`& > .${css.divider}`]: {
    marginInline: "auto",
  },

  // One level of section headings now everything sits inline, and one for items within them.
  h2: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.colors.sysTextDefault,
  },

  h3: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.4,
  },
}));
